import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Timer, ShieldCheck, Users, CalendarDays, Layers, ListChecks, ChevronRight, Flag } from "lucide-react";
import { useApp } from "@/context/AppContext";
import Countdown, { formatIST } from "@/components/Countdown";
import WavingFlag from "@/components/WavingFlag";
import { playClick } from "@/lib/sounds";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

function Particles() {
  const dots = Array.from({ length: 18 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((_, i) => {
        const colors = ["#FF9933", "#138808", "#003366"];
        const c = colors[i % 3];
        return (
          <span
            key={i}
            className="absolute rounded-full animate-float-slow"
            style={{
              width: 5 + (i % 4) * 3, height: 5 + (i % 4) * 3,
              left: `${(i * 53) % 100}%`, top: `${(i * 37) % 100}%`,
              background: c, opacity: 0.12 + (i % 3) * 0.05,
              animationDelay: `${i * 0.5}s`, animationDuration: `${6 + (i % 5)}s`,
            }}
          />
        );
      })}
    </div>
  );
}

export default function Landing() {
  const { config, user } = useApp();
  const comp = config?.competition;
  const schedule = config?.schedule || [];

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 tricolor-glow" />
        <div className="absolute inset-0 chakra-watermark" />
        <Particles />
        <div className="hidden lg:block absolute right-10 xl:right-24 top-8 z-10" data-testid="hero-flag">
          <WavingFlag width={150} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FF9933]/40 bg-[#FF9933]/10 text-[#E6892E] text-xs font-bold tracking-[0.2em] uppercase mb-6">
                <Flag className="w-3.5 h-3.5" /> {comp?.org || "LosTDRKC"} Presents
              </motion.div>
              <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
                className="text-5xl md:text-6xl lg:text-7xl font-black font-heading text-slate-900 leading-[1.02] tracking-tighter">
                Independence Day
                <br />
                <span className="text-gradient-tricolor">Special Quiz</span>
                <br />
                Competition
              </motion.h1>
              <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
                className="mt-6 text-base md:text-lg text-slate-500 max-w-xl font-medium">
                {comp?.tagline || "Test your knowledge. Beat the clock. Climb the leaderboard."}
              </motion.p>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-4">
                <Link to={user ? "/dashboard" : "/auth"} data-testid="hero-register-btn" onClick={playClick}
                  className="px-7 py-3.5 rounded-lg bg-[#FF9933] text-white font-bold hover:bg-[#E6892E] transition-colors shadow-lg shadow-[#FF9933]/25 inline-flex items-center gap-2">
                  {user ? "Open Dashboard" : "Register Now"} <ChevronRight className="w-4 h-4" />
                </Link>
                <Link to="/leaderboard" data-testid="hero-leaderboard-btn"
                  className="px-7 py-3.5 rounded-lg border-2 border-[#138808] text-[#138808] font-bold hover:bg-[#138808] hover:text-white transition-colors inline-flex items-center gap-2">
                  <Trophy className="w-4 h-4" /> View Leaderboard
                </Link>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
                <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#FF9933]" /> {comp ? formatIST(comp.start_at) : "15 Aug"} • 2:00 PM IST</span>
                <span className="inline-flex items-center gap-2"><Layers className="w-4 h-4 text-[#003366]" /> {schedule.length || 10} Categories</span>
                <span className="inline-flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#138808]" /> {schedule.reduce((a, c) => a + (c.question_count || 10), 0) || 100} Questions</span>
                <span className="inline-flex items-center gap-2"><Timer className="w-4 h-4 text-[#FF9933]" /> 60s per question</span>
              </motion.div>
            </div>
            <div className="md:col-span-5">
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
                className="relative bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl p-8 shadow-xl">
                <div className="absolute -top-px left-6 right-6 h-1 rounded-full tricolor-bar" />
                <div className="text-xs font-bold tracking-[0.3em] text-slate-400 uppercase mb-5 text-center">Competition Countdown</div>
                <Countdown targetIso={comp?.start_at} label={null} liveLabel="QUIZ IS LIVE 🇮🇳" />
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-center">
                    <div className="font-timer text-2xl font-bold text-[#FF9933]">{config?.scoring?.base_points ?? 100}+</div>
                    <div className="text-[10px] font-bold tracking-[0.15em] text-slate-400 uppercase">Points / Q</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-center">
                    <div className="font-timer text-2xl font-bold text-[#138808]">+{config?.scoring?.max_speed_bonus ?? 50}</div>
                    <div className="text-[10px] font-bold tracking-[0.15em] text-slate-400 uppercase">Speed Bonus</div>
                  </div>
                </div>
                <p className="mt-5 text-xs text-slate-400 text-center">Faster correct answers earn more points. The server is the source of truth for all timing.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
          <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase mb-3">Unlock Schedule</div>
          <h2 className="text-3xl md:text-4xl font-black font-heading text-slate-900 tracking-tighter">10 Categories. 100 Questions.<br className="hidden md:block" /> One Champion.</h2>
          <p className="mt-3 text-slate-500 max-w-2xl text-sm md:text-base">Categories unlock every {comp?.interval_minutes || 30} minutes starting {comp ? formatIST(comp.start_at) : ""}. Finish each category before the next wave of competitors catches up.</p>
        </motion.div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {schedule.map((c, i) => (
            <motion.div key={c.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
              data-testid={`schedule-card-${c.order}`}
              className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl p-5 min-h-[200px] flex flex-col hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-timer font-bold mb-4" style={{ background: c.color }}>
                {String(c.order).padStart(2, "0")}
              </div>
              <div className="font-heading font-bold text-slate-900 text-sm leading-snug">{c.name}</div>
              <div className="text-xs text-slate-400 mt-1 line-clamp-2">{c.description}</div>
              <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500">{formatIST(c.unlock_at)}</span>
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{c.question_count} Qs</span>
              </div>
              <div className="absolute inset-0 rounded-xl bg-white/95 backdrop-blur-xl border border-slate-200 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex flex-col justify-center overflow-hidden" data-testid={`schedule-hover-${c.order}`}>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: c.color }}>Category {String(c.order).padStart(2, "0")}</div>
                <div className="font-heading font-bold text-slate-900 mt-1 text-sm">{c.name}</div>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed line-clamp-4">{c.description}</p>
                <div className="mt-3 text-[11px] font-bold text-[#FF9933]">{formatIST(c.unlock_at)} IST • {c.question_count} Questions</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-slate-50 border-y border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 chakra-watermark" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Timer, title: "Server-Timed Questions", desc: "Each question has a separate countdown. You must answer before the timer runs out.", color: "#FF9933" },
              { icon: Trophy, title: "Live Leaderboard", desc: "Speed-based scoring with deterministic tie-breaking. Watch ranks update in real time.", color: "#138808" },
              { icon: ShieldCheck, title: "Anti-Cheat Shield", desc: "Switching tabs or leaving the quiz page is not allowed. Tab switching may be recorded and can lead to disqualification.", color: "#003366" },
              { icon: Users, title: "Community Powered", desc: "Built for the LosTDRKC community. Share your result card when you finish.", color: "#FF9933" },
            ].map((f, i) => (
              <motion.div key={f.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
                className="bg-white border border-slate-200 rounded-xl p-6">
                <f.icon className="w-6 h-6 mb-4" style={{ color: f.color }} />
                <div className="font-heading font-bold text-slate-900">{f.title}</div>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <h2 className="text-3xl md:text-5xl font-black font-heading tracking-tighter text-slate-900">Ready to play for the <span className="text-gradient-tricolor">Tricolor</span>?</h2>
          <p className="mt-4 text-slate-500 text-sm md:text-base">Registration is free. Pick a public display name — your real identity stays private.</p>
          <Link to={user ? "/dashboard" : "/auth"} data-testid="cta-register-btn" onClick={playClick}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors shadow-lg">
            {user ? "Go to Dashboard" : "Join the Competition"} <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
